import { config } from '@/config';
import { io } from 'socket.io-client';

// Graceful transport degradation: try WebSocket first, fall back to
// long-polling when WS is blocked (corporate proxies, mobile carrier
// WS filters, captive portals). We previously pinned to WebSocket-only,
// which surfaced as `transport close` toasts on hostile networks —
// see TCX-539, which observed ~2100 disconnects/24h on prod with no
// functional impact and no API restarts to blame.
//
// `tryAllTransports: true` is required: by default Engine.IO only
// attempts the first transport in the list on the initial connection,
// so listing `['websocket', 'polling']` alone wouldn't actually fall
// back when the WS handshake fails. With this flag, polling is tried
// next instead of failing the connection (codex review caught this).
//
// Polling is heavier (HTTP roundtrips every ~25s) but for our low-
// volume admin chat traffic the cost is negligible vs the resilience.
// localStorage can throw (privacy mode, sandboxed iframe, storage disabled) and
// is absent in non-browser/SSR contexts — read defensively so the handshake
// `auth` callback never throws and break the connection.
const readAccessToken = (): string | undefined => {
  if (typeof window === 'undefined') return undefined;
  try {
    return localStorage.getItem('accessToken') || undefined;
  } catch {
    return undefined;
  }
};

// Track the access token the socket last authenticated its handshake with, so
// syncSocketAuth only forces a re-handshake when it actually CHANGED. The
// sliding-window refresh writes an access token on many responses (see the
// axiosInstance interceptor); without this guard we'd disconnect/reconnect on
// every one — a reconnect storm. TCX-820.
let lastAuthToken = readAccessToken();

// Set when the app intentionally tears the socket down (logout). socket.io does
// NOT auto-reconnect after a manual disconnect, but syncSocketAuth could — a
// stale in-flight refresh response re-populates the token and would otherwise
// revive the socket as the just-logged-out user. While true, syncSocketAuth
// stays a no-op; an explicit login/reconnect clears it. TCX-820.
let intentionallyDisconnected = false;

const socket = io(config.VITE_SOCKET_ENDPOINT, {
  transports: ['websocket', 'polling'],
  tryAllTransports: true,
  autoConnect: true,
  reconnection: true,
  reconnectionDelay: 1000,
  reconnectionAttempts: Infinity,
  // The server authenticates the handshake from this token and derives the
  // user identity server-side (it no longer trusts a client-supplied userId).
  // The function form is re-evaluated on every (re)connect, so reconnects
  // always send the current token. Public pages connect without one
  // (unauthenticated) — the server gates the individual events, not the
  // connection.
  auth: (cb) => {
    lastAuthToken = readAccessToken();
    cb({ token: lastAuthToken });
  },
});

// Force a fresh handshake so a just-changed identity (login/activation)
// re-authenticates the socket with the current token. Records the token
// synchronously (before the async reconnect) so a burst of refresh callbacks
// can't each trigger a duplicate reconnect.
export const reconnectSocket = () => {
  intentionallyDisconnected = false;
  lastAuthToken = readAccessToken();
  socket.disconnect();
  socket.connect();
};

// Tear the socket down on logout and keep it down until an explicit
// login/reconnect. socket.io won't auto-reconnect after a manual disconnect;
// the intentionallyDisconnected flag additionally stops syncSocketAuth from
// reviving it if a stale in-flight refresh response lands after logout. TCX-820.
export const disconnectSocket = () => {
  intentionallyDisconnected = true;
  socket.disconnect();
};

// Re-authenticate the socket ONLY when the stored access token differs from the
// one it last handshaked with — i.e. after a silent token refresh (the axios
// interceptor) or a login. A no-op when unchanged, so the sliding-window
// refresh can call this on every response without a reconnect loop; and a
// socket that connected unauthenticated (no token at connect time)
// re-handshakes once a token becomes available. Never revives a socket that was
// intentionally disconnected on logout. TCX-820.
export const syncSocketAuth = () => {
  if (intentionallyDisconnected) return;
  if (readAccessToken() === lastAuthToken) return;
  reconnectSocket();
};

export default socket;